import { useEffect, useState } from 'react';
import {
  getPermissionState,
  isTurnNotificationsEnabled,
  notificationsSupported,
  requestPermission,
  setTurnNotificationsEnabled,
  type PermissionState,
} from './notification-prefs.js';

/**
 * "Notify me when it's my turn" toggle. Lives on the account
 * page. Handles the three-state permission model:
 *
 *   - unsupported : browser doesn't have Notification at all → hide.
 *   - default     : not asked yet → toggle prompts on click.
 *   - granted     : show a real toggle, persists to localStorage.
 *   - denied      : show disabled with a "blocked" hint.
 */
export function NotificationToggle() {
  const [permission, setPermission] = useState<PermissionState>('default');
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setPermission(getPermissionState());
    setEnabled(isTurnNotificationsEnabled());
  }, []);

  if (!notificationsSupported() || permission === 'unsupported') {
    return null;
  }

  async function onToggle() {
    if (!enabled) {
      // Turning ON: make sure permission is granted first.
      const next = await requestPermission();
      setPermission(next);
      if (next !== 'granted') return;
      setTurnNotificationsEnabled(true);
      setEnabled(true);
    } else {
      setTurnNotificationsEnabled(false);
      setEnabled(false);
    }
  }

  if (permission === 'denied') {
    return (
      <div className="ec-surface p-3 text-sm">
        <p className="text-ec-bone">Turn notifications are blocked by your browser.</p>
        <p className="mt-1 font-serif text-xs text-ec-bone-dim">
          To re-enable, open the site permissions in your browser settings and
          allow notifications.
        </p>
      </div>
    );
  }

  return (
    <label className="ec-surface flex max-w-md cursor-pointer items-start gap-3 p-3 text-sm">
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        className="mt-1 h-4 w-4 cursor-pointer rounded border-ec-line bg-ec-panel-2 text-ec-oxblood focus:ring-ec-brass"
      />
      <span>
        <span className="block font-display font-medium text-ec-face">Notify me when it's my turn</span>
        <span className="block font-serif text-xs text-ec-bone-dim">
          Pops a browser notification when an opponent makes a move and it
          becomes your turn — only fires while the tab is in the background,
          and never for matches against the AI bot.
        </span>
      </span>
    </label>
  );
}
